// src/config/modulos.js
// ═══════════════════════════════════════════════════════════════
// FUENTE ÚNICA DE VERDAD de los módulos de SwAlquiler.
// La usan: Sidebar.js (menú), App.js (guards de ruta) y
// Usuarios.js (checkboxes de permisos).
//
// Hay TRES capas de acceso, independientes entre sí:
//   1. PLAN     → tieneAccesoModulo() de useLimites (Trial/Básico sin Dashboard)
//   2. ROL      → soloAdmin / soloSuperAdmin
//   3. PERMISO  → bloqueable: el jefe decide por empleado (profiles.permisos)
//
// Para entrar a un módulo hay que pasar las tres.
//
// ⚠️ Si agregas un módulo aquí y es bloqueable, agrégalo TAMBIÉN a la lista
//    MODULOS_BLOQUEABLES de backend-emmita/routes/empleados.js
// ═══════════════════════════════════════════════════════════════

export const MODULOS = [
  // ─── Núcleo: nunca bloqueables ───────────────────────────────
  { id: "inicio", titulo: "Inicio", icono: "🏠", ruta: "/inicio", color: "#00B4D8" },
  { id: "crear", titulo: "Crear documento", icono: "📝", ruta: "/crear-documento", color: "#10b981" },
  { id: "clientes", titulo: "Clientes", icono: "👥", ruta: "/clientes", color: "#3b82f6" },
  { id: "inventario", titulo: "Inventario", icono: "📦", ruta: "/inventario", color: "#f59e0b" },
  { id: "trazabilidad", titulo: "Trazabilidad", icono: "📋", ruta: "/trazabilidad", color: "#14b8a6" },

  // ─── Bloqueables: el jefe decide por empleado ────────────────
  {
    id: "agenda", titulo: "Agenda", icono: "📅", ruta: "/agenda", color: "#ef4444",
    bloqueable: true,
    aviso: "No podrá ver el calendario ni agendar citas con clientes.",
  },
  {
    id: "proveedores", titulo: "Proveedores", icono: "🚚", ruta: "/proveedores", color: "#8b5cf6",
    bloqueable: true,
    aviso: "No podrá consultar proveedores ni sus productos al armar un pedido.",
  },
  {
    id: "buscar-doc", titulo: "Buscar documento", icono: "🔍", ruta: "/buscar-documento", color: "#06b6d4",
    bloqueable: true,
    aviso: "No podrá buscar ni reimprimir cotizaciones y pedidos anteriores.",
  },
  {
    id: "reportes", titulo: "Dashboard", icono: "📊", ruta: "/reportes", color: "#ec4899",
    bloqueable: true,
    plan: "reportes",
    aviso: "No verá las gráficas de ventas ni los indicadores del negocio.",
  },
  {
    id: "contabilidad", titulo: "Contabilidad", icono: "💰", ruta: "/contabilidad", color: "#22c55e",
    bloqueable: true,
    aviso: "No verá ingresos, gastos, utilidades ni la rentabilidad del negocio.",
  },
  {
    id: "recepcion", titulo: "Recepción", icono: "📥", ruta: "/recepcion", color: "#6366f1",
    bloqueable: true,
    aviso: "No podrá registrar devoluciones de mercancía ni devolver garantías.",
  },
  {
    id: "buscar-recep", titulo: "Buscar recepción", icono: "🔎", ruta: "/buscar-recepcion", color: "#a855f7",
    bloqueable: true,
    aviso: "No podrá consultar recepciones ya registradas.",
  },

  // ─── Cuenta propia: SIEMPRE accesible para todos ─────────────
  // Vive aparte de "Usuarios" a propósito: aquí se cambia la contraseña,
  // y bloquear eso dejaría a un empleado sin poder actualizar su clave.
  { id: "mi-perfil", titulo: "Mi Perfil", icono: "🙋", ruta: "/mi-perfil", color: "#0ea5e9" },

  // ─── Solo administradores ────────────────────────────────────
  { id: "usuarios", titulo: "Usuarios", icono: "👤", ruta: "/usuarios", color: "#64748b", soloAdmin: true },
  { id: "mi-empresa", titulo: "Mi Empresa", icono: "🏢", ruta: "/mi-empresa", color: "#0077B6", soloAdmin: true },

  // ─── Solo super_admin (Jorge) ────────────────────────────────
  { id: "superadmin", titulo: "Super Admin", icono: "🛡️", ruta: "/superadmin", color: "#dc2626", soloSuperAdmin: true },
];

// Los que aparecen como checkbox en la pantalla de permisos.
export const MODULOS_BLOQUEABLES = MODULOS.filter((m) => m.bloqueable);

/**
 * Lee la lista de módulos bloqueados de un perfil.
 * Se guarda SOLO lo bloqueado (no lo permitido) a propósito: así, si mañana
 * agregamos un módulo nuevo, queda permitido para todos sin migrar nada.
 */
export function getModulosBloqueados(perfil) {
  const lista = perfil?.permisos?.modulos_bloqueados;
  return Array.isArray(lista) ? lista : [];
}

/**
 * ¿Este usuario puede ver este módulo? Evalúa las tres capas.
 * @param {object} modulo - un elemento de MODULOS
 * @param {object} ctx - { esAdmin, esSuperAdmin, perfil, tieneAccesoModulo }
 */
export function puedeVerModulo(modulo, ctx) {
  const { esAdmin, esSuperAdmin, perfil, tieneAccesoModulo } = ctx;

  // Capa 2 — Rol
  if (modulo.soloSuperAdmin && !esSuperAdmin) return false;
  if (modulo.soloAdmin && !esAdmin) return false;

  // Capa 1 — Plan del tenant
  if (modulo.plan && typeof tieneAccesoModulo === "function") {
    if (!tieneAccesoModulo(modulo.plan)) return false;
  }

  // Capa 3 — Permiso por empleado. Los admins nunca se autobloquean.
  if (modulo.bloqueable && !esAdmin) {
    if (getModulosBloqueados(perfil).includes(modulo.id)) return false;
  }

  return true;
}

/** Busca un módulo por su ruta. Útil para los guards de App.js. */
export function moduloPorRuta(ruta) {
  return MODULOS.find((m) => m.ruta === ruta);
}